import { useEffect, useRef } from 'react';

const normalizeKey = (event) => {
    try {
        if (!event || typeof event.key !== 'string') return '';
        const key = event.key.toLowerCase();
        if (key === 'escape') return 'esc';
        if (key === ' ') return 'space';
        if (key === 'arrowup') return 'up';
        if (key === 'arrowdown') return 'down';
        if (key === 'enter') return 'enter';
        if (key === 'backspace') return 'backspace';
        return key;
    } catch (error) {
        return '';
    }
};

const isEditableTarget = (target) => {
    if (!target) return false;
    const tag = target.tagName && typeof target.tagName === 'string' ? target.tagName.toLowerCase() : '';
    return tag === 'input' || tag === 'textarea' || target.isContentEditable;
};

export default function useHotkeys(map = {}) {
    const sequenceRef = useRef([]);
    const timeoutRef = useRef(null);
    const mapRef = useRef(map);
    mapRef.current = map;

    useEffect(() => {
        const sequences = Object.entries(mapRef.current)
            .filter(([combo]) => combo.includes(' '))
            .map(([combo, handler]) => ({
                keys: combo.split(' ').map((key) => key.trim()),
                handler,
            }));

        const combos = Object.entries(mapRef.current)
            .filter(([combo]) => !combo.includes(' '))
            .reduce((acc, [combo, handler]) => {
                acc[combo] = handler;
                return acc;
            }, {});

        const resetSequence = () => {
            sequenceRef.current = [];
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };

        const handleKeydown = (event) => {
            const key = normalizeKey(event);
            const isMod = event.metaKey || event.ctrlKey;
            const comboKey = isMod && key.length === 1 ? `mod+${key}` : key;

            if (combos[comboKey]) {
                if (!isEditableTarget(event.target) || comboKey === 'esc') {
                    event.preventDefault();
                    combos[comboKey](event);
                    resetSequence();
                    return;
                }
            }

            if (combos[key]) {
                if (!isEditableTarget(event.target) || key === 'esc') {
                    event.preventDefault();
                    combos[key](event);
                    resetSequence();
                    return;
                }
            }

            if (key.length === 1 && !isMod) {
                sequenceRef.current = [...sequenceRef.current, key].slice(-2);
                const current = sequenceRef.current;
                const matched = sequences.find((seq) => seq.keys.join(' ') === current.join(' '));
                const prefix = sequences.some((seq) => seq.keys[0] === current[0]);

                if (matched) {
                    if (!isEditableTarget(event.target)) {
                        event.preventDefault();
                        matched.handler(event);
                    }
                    resetSequence();
                    return;
                }

                if (prefix) {
                    if (timeoutRef.current) clearTimeout(timeoutRef.current);
                    timeoutRef.current = setTimeout(() => resetSequence(), 800);
                } else {
                    resetSequence();
                }
            }
        };

        window.addEventListener('keydown', handleKeydown);
        return () => {
            window.removeEventListener('keydown', handleKeydown);
            resetSequence();
        };
    }, []);
}
